import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiCheck, FiMinus, FiZap } from 'react-icons/fi';
import { graphqlRequest } from '../utils/graphqlRequest';
import { adminPath, ADMIN_ROUTES } from '../constants/adminPortal';

const GET_PLANS = `
  query GetBillingPlans {
    getBillingPlans
  }
`;

const START_REACTIVATION = `
  mutation StartBillingReactivation($priceId: String!) {
    startBillingReactivation(priceId: $priceId)
  }
`;

const ONBOARDING_SERVICES = [
  { name: 'DIY Setup', price: 'Free', text: 'Set up your account at your own pace with guides and templates.' },
  { name: 'Guided Onboarding', price: '$299', text: 'A specialist helps configure your core workflows.' },
  { name: 'Premium Migration', price: '$999', text: 'We move customer, staff and operational data for you.' },
  { name: 'Enterprise Rollout', price: 'Custom', text: 'A tailored multi-branch rollout and training plan.' },
];

const perMonth = (amount, interval) => (interval === 'year' ? amount / 12 : amount);

export default function BillingPlans({ overview, formatMoney, ended = false, billingOnly = false, onChangePlan }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState('');
  const [cycle, setCycle] = useState(overview.plan.interval === 'year' ? 'year' : 'month');
  const [onlyDiffs, setOnlyDiffs] = useState(false);
  const [changingTo, setChangingTo] = useState('');

  useEffect(() => {
    graphqlRequest(GET_PLANS)
      .then((res) => setPlans(res?.getBillingPlans || []))
      .catch((e) => setError(e?.message || 'Failed to load plans.'));
  }, []);

  const visiblePlans = useMemo(
    () => (plans || []).filter((p) => p.isCustom || p.prices.some((pr) => pr.interval === cycle)),
    [plans, cycle],
  );

  const currentPlan = (plans || []).find((p) => p.prices.some((pr) => pr.id === overview.plan.priceId));
  const currentPerMonth = perMonth(overview.plan.amount, overview.plan.interval);

  // "Save N months" on the Annual toggle — best saving across plans.
  const savedMonths = useMemo(
    () =>
      Math.max(
        0,
        ...(plans || []).map((p) => {
          const m = p.prices.find((pr) => pr.interval === 'month');
          const y = p.prices.find((pr) => pr.interval === 'year');
          return m && y && m.amount > 0 ? Math.round((m.amount * 12 - y.amount) / m.amount) : 0;
        }),
      ),
    [plans],
  );

  // Comparison table — same inheritance rule as the website pricing page:
  // a plan includes every feature of the lower-positioned plans; custom plans include all.
  const { rows, includes } = useMemo(() => {
    const own = (p) => [
      ...new Set([...p.features, ...(p.isCustom ? p.compareFeatures.custom : p.compareFeatures[cycle] || [])]),
    ];
    const sets = {};
    visiblePlans.forEach((p) => {
      const lower = visiblePlans.filter(
        (o) => !o.isCustom && (p.isCustom || (o.sortOrder ?? Infinity) < (p.sortOrder ?? -Infinity)),
      );
      sets[p.name] = new Set([...lower.flatMap(own), ...own(p)]);
    });
    return { rows: [...new Set(visiblePlans.flatMap(own))], includes: sets };
  }, [visiblePlans, cycle]);

  const shownRows = onlyDiffs
    ? rows.filter((f) => !visiblePlans.every((p) => includes[p.name].has(f)))
    : rows;

  const openTicket = (subject, description) =>
    navigate(adminPath(ADMIN_ROUTES.NEW_TICKET), {
      state: { subject, category: 'billing', description },
    });

  // Subscription ended: any plan starts a new subscription through Stripe Checkout.
  const reactivate = async (price) => {
    setChangingTo(price.id);
    try {
      const res = await graphqlRequest(START_REACTIVATION, { priceId: price.id });
      window.location.assign(res.startBillingReactivation.url);
    } catch (e) {
      toast.error(e?.message || 'Could not start reactivation.');
      setChangingTo('');
    }
  };

  const renderAction = (plan, price) => {
    if (plan.isCustom && billingOnly) return null; // support tickets aren't available while ended
    if (ended && !plan.isCustom) {
      return (
        <button type="button" className="bs-btn bs-btn--primary" disabled={Boolean(changingTo)} onClick={() => reactivate(price)}>
          {changingTo === price.id ? 'Opening…' : 'Reactivate'}
        </button>
      );
    }
    if (plan.isCustom) {
      return (
        <button type="button" className="bs-btn" onClick={() => openTicket(`${plan.name} plan enquiry`, `We'd like to discuss the ${plan.name} plan for our business.`)}>
          Contact sales
        </button>
      );
    }
    if (price.id === overview.plan.priceId) {
      return <button type="button" className="bs-btn" disabled>Current plan</button>;
    }
    const label =
      plan === currentPlan
        ? cycle === 'year' ? 'Switch to Annual' : 'Switch to Monthly'
        : perMonth(price.amount, price.interval) > currentPerMonth ? 'Upgrade' : 'Downgrade';
    return (
      <button
        type="button"
        className={`bs-btn ${label === 'Downgrade' ? '' : 'bs-btn--primary'}`}
        onClick={() => onChangePlan(price.id)}
      >
        {label}
      </button>
    );
  };

  if (error) return <p className="bs-text-danger">{error}</p>;
  if (!plans) return <p className="bs-muted">Loading plans…</p>;

  return (
    <>
      <div className="bs-plans-head">
        <div>
          <h3>Find the plan that fits your business</h3>
          <p className="bs-muted bs-small">Upgrade, downgrade or change your billing cycle at any time.</p>
        </div>
        <div className="bs-toggle" role="tablist" aria-label="Billing cycle">
          {['month', 'year'].map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={cycle === c}
              className={cycle === c ? 'active' : ''}
              onClick={() => setCycle(c)}
            >
              {c === 'month' ? 'Monthly' : 'Annual'}
            </button>
          ))}
          {savedMonths > 0 && (
            <span className="bs-badge bs-badge--green">Save {savedMonths} month{savedMonths === 1 ? '' : 's'}</span>
          )}
        </div>
      </div>

      <div className="bs-plan-cards">
        {visiblePlans.map((plan) => {
          const price = plan.prices.find((pr) => pr.interval === cycle);
          const isCurrent = plan === currentPlan;
          const isPopular = plan.name.toLowerCase() === 'growth';
          return (
            <section key={plan.name} className={`bs-card bs-plan-card ${isPopular ? 'bs-plan-card--popular' : ''}`}>
              {isPopular && <div className="bs-plan-ribbon">Most popular</div>}
              <div className="bs-plan-name">
                <h4>{plan.name}</h4>
                {isCurrent && <span className="bs-badge bs-badge--purple">Current plan</span>}
              </div>
              <div className="bs-price bs-plan-card-price">
                {plan.isCustom || !price ? (
                  <><strong>Custom</strong><span> Tailored pricing</span></>
                ) : (
                  <><strong>{formatMoney(price.amount, price.currency, true)}</strong><span> / {price.interval}</span></>
                )}
              </div>
              <div className="bs-plan-staff">
                <strong>
                  {plan.totalStaff === 'unlimited' || plan.isCustom
                    ? 'Unlimited options'
                    : plan.totalStaff
                      ? `Up to ${plan.totalStaff} staff included`
                      : 'Staff included'}
                </strong>
                <span>
                  {plan.pricePerStaff
                    ? `Extra staff ${formatMoney(plan.pricePerStaff, price?.currency || 'usd')}/user/month`
                    : plan.isCustom ? 'Volume pricing available' : ''}
                </span>
              </div>
              <ul className="bs-features bs-features--single">
                {plan.features.map((f) => (
                  <li key={f}><span className="bs-check"><FiCheck /></span>{f}</li>
                ))}
              </ul>
              <div className="bs-plan-card-action">{renderAction(plan, price)}</div>
            </section>
          );
        })}
      </div>

      {rows.length > 0 && (
        <section className="bs-card bs-compare">
          <div className="bs-plan-head">
            <div>
              <p className="bs-eyebrow">Full comparison</p>
              <h4 className="bs-staff-title">Compare all features</h4>
            </div>
            <label className="bs-switch">
              <span className="bs-muted bs-small">Show only differences</span>
              <input type="checkbox" checked={onlyDiffs} onChange={(e) => setOnlyDiffs(e.target.checked)} />
              <span className="bs-switch-track" aria-hidden="true" />
            </label>
          </div>
          <div className="bs-table-wrap">
            <table className="bs-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  {visiblePlans.map((p) => (
                    <th key={p.name} className={p === currentPlan ? 'bs-col-current' : ''}>{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shownRows.map((feature) => (
                  <tr key={feature}>
                    <td>{feature}</td>
                    {visiblePlans.map((p) => (
                      <td key={p.name} className={p === currentPlan ? 'bs-col-current' : ''}>
                        {includes[p.name].has(feature) ? (
                          <FiCheck className="bs-yes" aria-label="Included" />
                        ) : (
                          <FiMinus className="bs-no" aria-label="Not included" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!billingOnly && (<>
      <h3 className="bs-subheading">Setup and onboarding services</h3>
      <p className="bs-muted bs-small">Get the level of support your team needs.</p>
      <div className="bs-plan-cards bs-services">
        {ONBOARDING_SERVICES.map((s) => (
          <section key={s.name} className="bs-card">
            <span className="bs-stat-icon bs-stat-icon--purple"><FiZap /></span>
            <p className="bs-service-name">{s.name}</p>
            <strong className="bs-service-price">{s.price}</strong>
            <p className="bs-muted bs-small">{s.text}</p>
            <button
              type="button"
              className="bs-btn"
              onClick={() => openTicket(`${s.name} request`, `We'd like to request ${s.name} (${s.price}). ${s.text}`)}
            >
              Request
            </button>
          </section>
        ))}
      </div>
      </>)}
    </>
  );
}
